import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import useExtraSmallScreen from 'hooks/useExtraSmallScreen';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import DialogContent from 'housecall-ui/dist/DesignSystem/Feedback/Dialog/DialogContent';
import CircularProgress from 'housecall-ui/dist/DesignSystem/Feedback/Progress/CircularProgress';
import Switch from 'housecall-ui/dist/DesignSystem/Inputs/Switches/Switch';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Hidden from 'housecall-ui/dist/DesignSystem/Layout/Hidden/Hidden';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import { isEmpty } from 'lodash-es';

import PayWallActionsLayout from 'components/PayWall/PayWallActionsLayout';
import PayWallDialogTitle from 'components/PayWall/PayWallDialogTitle';
import PayWallPlanSelectionContent from 'components/PayWall/PayWallPlanSelectionContent';
import {
  DURATION_SELECTED,
  fetchUpdatePlanPreview,
  navigateToAddonsScreen,
  PLAN_SELECTED,
} from 'components/PayWall/Redux/Actions';
import { PayWallDuration } from 'components/PayWall/Redux/Reducer.type';
import {
  getPaywallSpinnerState,
  getPlanGroupsWithIncrementalFeatures,
  getRequiredPlan,
  getSelectedDuration,
  getSelectedPlanV3,
} from 'components/PayWall/Redux/Selectors';
import { identifyWithPlanInterestedIn } from 'components/Services/Analytics/IdentifyPropertiesService';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';

import CancelButton from './PayWallCancelButton';
import {
  CIRCULAR_PROGRESS_BUTTON_SIZE,
  PAY_WALL_VERSION,
  useButtonProgressStyles,
  useDialogContentStyles,
} from './PayWallDialog';
import SubmitButton from './PayWallSubmitButton';

function usePlanSelected() {
  const dispatch = useDispatch();
  const { trackEvent } = useTrackEventsService();

  return useCallback(
    (planSelected) => {
      dispatch(identifyWithPlanInterestedIn(planSelected.tier));
      dispatch({ type: PLAN_SELECTED, planSelected });
      trackEvent('Plan selected', { plan_tier: planSelected.tier, payWallVersion: PAY_WALL_VERSION });
    },
    [dispatch, trackEvent]
  );
}

function useNavigateToAddons() {
  const dispatch = useDispatch();
  const { id: selectedPlanId } = useSelector(getSelectedPlanV3);

  return useCallback(() => {
    dispatch(navigateToAddonsScreen(selectedPlanId));
  }, [dispatch, selectedPlanId]);
}

type Props = {
  onCancel: () => void;
};

const usePlanDurationStyles = makeStyles((theme: HCPTheme) => ({
  label: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  inactive: {
    color: theme.palette.secondary.light,
  },
}));

export const PayWallPlanDurationSwitch = () => {
  const dispatch = useDispatch();
  const isExtraSmallScreen = useExtraSmallScreen();
  const { trackEvent } = useTrackEventsService();

  const selectedDuration = useSelector(getSelectedDuration);
  const classes = usePlanDurationStyles();

  const handleChange = (checked: boolean) => {
    const duration = checked ? PayWallDuration.Annual : PayWallDuration.Monthly;
    dispatch({ type: DURATION_SELECTED, duration });
    dispatch(fetchUpdatePlanPreview({ planType: duration }));
    trackEvent('Plan duration switched', { planType: duration, payWallVersion: PAY_WALL_VERSION });
  };

  return (
    <Box
      display="flex"
      flexDirection={isExtraSmallScreen ? 'column' : 'row'}
      justifyContent="space-between"
      alignItems="center"
      data-testid="cadence-switch"
    >
      <Hidden xsDown>
        <Typography
          variant="body1"
          onClick={() => handleChange(false)}
          className={classNames(classes.label, { [classes.inactive]: selectedDuration === PayWallDuration.Annual })}
        >
          Billed monthly
        </Typography>
      </Hidden>
      <Switch
        checked={selectedDuration === PayWallDuration.Annual}
        name="selectedDuration"
        color="primary"
        onChange={(_: unknown, checked: boolean) => {
          handleChange(checked);
        }}
      />
      <Hidden xsDown>
        <Typography
          variant="body1"
          onClick={() => handleChange(true)}
          className={classNames(classes.label, { [classes.inactive]: selectedDuration === PayWallDuration.Monthly })}
        >
          Billed annually
        </Typography>
      </Hidden>
      <Hidden smUp>
        <Typography variant="caption">Monthly / Annualy</Typography>
      </Hidden>
    </Box>
  );
};

export default function PayWallPlanSelectionDialog(props: Props): JSX.Element {
  const { onCancel } = props;

  const planGroups = useSelector(getPlanGroupsWithIncrementalFeatures);

  const selectedPlan = useSelector(getSelectedPlanV3);
  const required = useSelector(getRequiredPlan);
  const loading = useSelector(getPaywallSpinnerState);
  const classes = useButtonProgressStyles({ circularProgressSize: CIRCULAR_PROGRESS_BUTTON_SIZE });
  const dialogContentClasses = useDialogContentStyles({ isLoading: false });

  const navigateToAddons = useNavigateToAddons();
  const planSelected = usePlanSelected();

  const submitButton = (
    <Box className={classes.wrapper}>
      <SubmitButton disabled={isEmpty(selectedPlan) || loading} onClick={() => navigateToAddons()}>
        Next
      </SubmitButton>
      {loading && <CircularProgress className={classes.buttonProgress} size={CIRCULAR_PROGRESS_BUTTON_SIZE} />}
    </Box>
  );

  const initialized = planGroups.length > 0;

  if (!initialized) {
    return (
      <>
        <PayWallDialogTitle>Pick your plan</PayWallDialogTitle>
        <DialogContent classes={dialogContentClasses}>
          <CircularProgress className={classes.buttonProgress} />
        </DialogContent>
        <PayWallActionsLayout cancelButton={<CancelButton onClick={onCancel} />} />
      </>
    );
  }

  return (
    <>
      <PayWallDialogTitle titleAddon={<PayWallPlanDurationSwitch />}>Pick your plan</PayWallDialogTitle>
      <PayWallPlanSelectionContent planGroups={planGroups} required={required} planSelected={planSelected} />
      <PayWallActionsLayout submitButton={submitButton} cancelButton={<CancelButton onClick={onCancel} />} />
    </>
  );
}
